import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { StatusPill } from '@/components/px/StatusPill'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import type { AuditEntry } from '@/features/audit/types'
import { formatDateTime } from '@/lib/format'

const columnHeadClass =
  'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'

/** One audit row: when, action, entity, actor, and collapsible details JSON. */
function AuditRow({ row }: { row: AuditEntry }) {
  const { t } = useTranslation('audit')
  const [detailsOpen, setDetailsOpen] = useState(false)

  const entity =
    row.entityType !== undefined || row.entityId !== undefined
      ? [row.entityType, row.entityId].filter((part) => part !== undefined && part !== '').join(' · ')
      : '—'

  const onToggleClicked = (): void => setDetailsOpen((open) => !open)

  return (
    <TableRow>
      <TableCell className="whitespace-nowrap py-2.5 text-sm tabular-nums text-foreground">
        {formatDateTime(row.occurredAt) ?? '—'}
      </TableCell>
      <TableCell>
        {row.action !== undefined && row.action !== '' ? (
          <StatusPill tone="proposed">{row.action}</StatusPill>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="max-w-[18rem] truncate text-sm text-ink-soft">{entity}</TableCell>
      <TableCell className="max-w-[14rem] truncate font-mono text-xs text-ink-soft">
        {row.actorUserId ?? '—'}
      </TableCell>
      <TableCell>
        {row.details !== undefined && Object.keys(row.details).length > 0 ? (
          <>
            <button
              type="button"
              onClick={onToggleClicked}
              aria-expanded={detailsOpen}
              className="rounded-md border border-input px-2 py-1 text-xs text-ink-soft hover:bg-muted"
            >
              {t('detailsToggle')}
            </button>
            {detailsOpen && (
              <pre className="mt-2 max-w-[24rem] overflow-x-auto rounded-md bg-muted px-2 py-1.5 font-mono text-xs text-ink-soft">
                {JSON.stringify(row.details, null, 2)}
              </pre>
            )}
          </>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </TableCell>
    </TableRow>
  )
}

/** The audit log table (PX-W6.2): server rows, newest first. */
export function AuditTable({ rows }: { rows: readonly AuditEntry[] }) {
  const { t } = useTranslation('audit')
  return (
    <Table>
      <TableCaption className="sr-only">{t('title')}</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead className={columnHeadClass}>{t('columns.time')}</TableHead>
          <TableHead className={columnHeadClass}>{t('columns.action')}</TableHead>
          <TableHead className={columnHeadClass}>{t('columns.entity')}</TableHead>
          <TableHead className={columnHeadClass}>{t('columns.actor')}</TableHead>
          <TableHead className={columnHeadClass}>{t('columns.details')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, index) => (
          <AuditRow key={row.id ?? index} row={row} />
        ))}
      </TableBody>
    </Table>
  )
}
